<template>
  <b-table
    :data="data"
    :checked-rows.sync="checkedRows"
    :detailed="detailed"
    :custom-detail-row="detailed"
    :has-detailed-visible="isGrouped"
    ref="table"
    striped
    narrowed
    hoverable
    sort-icon="angle-up"
    :checkable="!bulkOpsDisabled"
    paginated
    per-page="20"
    default-sort="score"
    default-sort-direction="desc"
  >
    <b-table-column
      :label="$t('compCarbonFormsModelTable.label-3')"
      field="score"
      sortable
      :custom-sort="sortValid"
      centered
      v-slot="props"
    >
      <slot name="validity" v-bind:props="props">
        <b-tag rounded v-if="props.row.score === 1" type="is-success">{{
          $t('compCarbonFormsModelTable.label-4')
        }}</b-tag>
        <b-tag rounded v-else-if="props.row.score === 2" type="is-danger">{{
          $t('compCarbonFormsModelTable.label-5')
        }}</b-tag>
        <b-tag rounded v-else-if="props.row.score === 3" type="is-warning">{{
          $t('incomplete')
        }}</b-tag>
      </slot>
    </b-table-column>
    <b-table-column
      v-if="withTags"
      :label="$t('tags')"
      field="tags"
      sortable
      centered
      v-slot="props"
    >
      <my-tag-list
        v-if="props.row.isGrouped"
        :tags="props.row.items[0].tags"
        :limit="limit"
      />
      <my-tag-list v-else :tags="props.row.tags" :limit="limit" />
    </b-table-column>
    <slot />
    <b-table-column width="80" v-slot="props">
      <div class="panel-block">
        <b-tooltip
          :label="disabled || singleUpdateDisabled ? $t('view') : $t('edit')"
          type="is-black"
        >
          <b-button
            size="is-small"
            :icon-left="disabled || singleUpdateDisabled ? 'search' : 'pencil'"
            :disabled="forceDisabled(props.row)"
            @click="$emit('edit', props.row)"
          />
        </b-tooltip>
        <slot name="delete" v-bind:props="props">
          <b-tooltip :label="$t('delete')" type="is-black">
            <b-button
              size="is-small"
              icon-left="trash"
              :disabled="disabled"
              @click="del([props.row])"
            />
          </b-tooltip>
        </slot>
      </div>
    </b-table-column>
    <section slot="empty" class="section">
      <div class="content has-text-grey has-text-centered">
        <p>
          <i class="fa fa-ban" style="font-size: 64px"></i>
        </p>
        <p>
          {{
            noItemLabel ? noItemLabel : $t('compCarbonFormsModelTable.no-item')
          }}
        </p>
      </div>
    </section>
    <template slot="detail" slot-scope="props">
      <slot name="detail" v-bind:props="props" />
    </template>
    <template #footer v-if="!bulkOpsDisabled">
      <div :style="{ display: 'inline-block' }">
        <b-icon
          :style="{
            position: 'relative',
            top: '2px',
            left: leftPosition,
            'margin-right': marginRight
          }"
          size="is-small"
          icon="caret-up"
        />
        <span
          :style="{ position: 'relative', top: '4px', 'margin-right': '10px' }"
        >
          <em>{{ $t('compCarbonFormsModelTable.label-1') }} :</em>
        </span>
        <b-tooltip
          :label="$t('compCarbonFormsModelTable.label-2')"
          type="is-black"
        >
          <b-button
            v-if="withTags"
            :disabled="checkedRows.length === 0 || disabled"
            size="is-small"
            icon-left="tags"
            @click="active = true"
          />
        </b-tooltip>
        <b-tooltip :label="$t('delete')" type="is-black">
          <b-button
            :disabled="checkedRows.length === 0 || disabled"
            size="is-small"
            icon-left="trash"
            @click="del(checkedRows)"
          />
        </b-tooltip>
      </div>
    </template>
    <tags-selector-modal
      :active="active"
      :items="checkedRows"
      :module="module"
      @close="active = false"
      @tags-added="tagsAdded"
    />
  </b-table>
</template>

<script>
export default {
  name: 'Table',
  props: {
    data: {
      type: Array,
      default: function () {
        return []
      }
    },
    limit: {
      type: Number,
      default: 1
    },
    module: {
      type: String,
      default: null
    },
    detailed: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    forceDisabled: {
      type: Function,
      default: function (row) {
        return false
      }
    },
    noItemLabel: {
      type: String
    },
    /**
     * True whether the bulk operations must be hidden
     * at the botton of the table
     */
    bulkOpsDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * True whether the items are read-only
     * items can be seen but not modified
     */
    singleUpdateDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * True whether to include tag column and tag bulk operation (at the bottom
     * of the table)
     */
    withTags: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update', 'del', 'edit'],
  components: {
    'my-tag-list': () => import('@/components/core/MyTagList.vue'),
    'tags-selector-modal': () =>
      import('@/components/core/TagsSelectorModal.vue')
  },
  data () {
    return {
      checkedRows: [],
      active: false
    }
  },
  computed: {
    leftPosition: function () {
      if (this.detailed) {
        return '45px'
      } else {
        return '2px'
      }
    },
    marginRight: function () {
      if (this.detailed) {
        return '55px'
      } else {
        return '10px'
      }
    }
  },
  methods: {
    sortStrToNum (a, b, isAsc) {
      if (isAsc) {
        return Number(a) - Number(b)
      } else {
        return Number(b) - Number(a)
      }
    },
    sortValid (a, b, isAsc) {
      return this.sortStrToNum(a.score, b.score, isAsc)
    },
    tagsAdded: function () {
      this.checkedRows = []
      this.$emit('update')
    },
    isGrouped: function (row) {
      return row.isGrouped
    },
    del: function (items) {
      let self = this
      let msg =
        this.$t('compCarbonFormsModelTable.delete-msg-1') +
        '<strong>' +
        items.length +
        this.$t('compCarbonFormsModelTable.delete-msg-2') +
        '</strong> ?'
      if (items.length === 1) {
        msg = this.$t('compCarbonFormsModelTable.delete-msg-3')
      }
      this.$buefy.dialog.confirm({
        title: self.$t('deletion'),
        message: msg,
        confirmText: self.$t('validate'),
        cancelText: self.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: function () {
          self.$emit('del', items)
          self.$emit('update')
          self.checkedRows = []
        }
      })
    }
  }
}
</script>

<style>
.table td {
  vertical-align: middle !important;
}
span.warning {
  color: hsl(348, 100%, 61%);
}
</style>
